import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Bike, MapPin, Phone, Printer, ChevronDown, ChevronUp,
  User, Package, ExternalLink, Sun, Sunset, Filter, Search,
  LayoutList, Layers,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Select, Label, Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { toast } from "../components/ui/Toast";
import PrintPreview from "../components/ui/PrintPreview";
import { PhieuXuatKho } from "../components/print/BillTemplates";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, formatDateTime, parseApiError} from "../lib/utils";
import { useSort } from "../hooks/useSort";
import { cn } from "../lib/utils";

const PAY_LABEL = { debt: "Công nợ", cash: "Tiền mặt", transfer: "Chuyển khoản", ewallet: "Ví điện tử", card: "Thẻ" };
const SHIFT_MORNING   = "morning";
const SHIFT_AFTERNOON = "afternoon";

// Ca sáng: 8:00–12:30 / Ca chiều: 12:30–18:00
const SHIFTS = [
  { id: SHIFT_MORNING,   label: "Ca Sáng",  time: "8:00 – 12:30",  color: "amber",  Icon: Sun },
  { id: SHIFT_AFTERNOON, label: "Ca Chiều", time: "12:30 – 18:00", color: "blue",   Icon: Sunset },
];

function ShiftBadge({ shift }) {
  if (!shift) return <span className="text-xs text-ink-muted italic">—</span>;
  const s = SHIFTS.find(x => x.id === shift);
  if (!s) return null;
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full",
      s.color === "amber" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
    )}>
      <s.Icon size={9} /> {s.label}
    </span>
  );
}

const stripAccents = (s) => { if (!s) return ''; return String(s).toLowerCase().replace(/đ/g, 'd').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); };

export default function Delivery() {
  const { t } = useI18n();
  const [orders,   setOrders]   = useState([]);
  const [shippers, setShippers] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [printOrder, setPrintOrder] = useState(null); // đơn đang xem preview in
  const [shopInfo,   setShopInfo]   = useState({});   // settings shop

  // Bộ lọc
  const [filterDate,    setFilterDate]    = useState(new Date().toISOString().slice(0, 10));
  const [filterShipper, setFilterShipper] = useState("");
  const [filterProduct, setFilterProduct] = useState(""); // tên/chủng loại sản phẩm
  const [filterMinQty,  setFilterMinQty]  = useState("");  // số lượng tối thiểu
  const [searchQ,       setSearchQ]       = useState("");

  // Tab ca đang xem (null = tất cả)
  const [activeShift, setActiveShift] = useState(null);

  // Expand
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // View mode: "customer" | "product"
  const [viewMode, setViewMode] = useState("customer");
  // Expand trong view sản phẩm
  const [expandedProducts, setExpandedProducts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: filterDate, date_to: filterDate };
      const [o1, o2, s, sh] = await Promise.all([
        api.get("/orders", { params: { ...params, status: "new" } }),
        api.get("/orders", { params: { ...params, status: "delivering" } }),
        api.get("/users/shippers"),
        api.get("/settings"),
      ]);
      setOrders([...(o1.data || []), ...(o2.data || [])]);
      setShippers(s.data || []);
      setShopInfo(sh.data || {});
    } catch {
      toast({ title: "Lỗi tải dữ liệu", variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterDate]);

  // Phân shipper — đồng bộ tất cả đơn cùng khách trong ngày
  const assignShipper = async (orderId, shipperId, customerKey) => {
    try {
      const sameCustomerOrders = customerKey
        ? orders.filter((o) => (o.customer_id || `${o.customer_name}_${o.customer_phone}`) === customerKey)
        : [orders.find((o) => o.order_id === orderId)].filter(Boolean);
      await Promise.all(
        sameCustomerOrders.map((o) =>
          api.put(`/orders/${o.order_id}/assign-shipper?shipper_id=${shipperId}`)
        )
      );
      toast({ title: sameCustomerOrders.length > 1 ? `Đã phân công ${sameCustomerOrders.length} đơn cho cùng khách` : "Đã phân công", variant: "success" });
      load();
    } catch (e) {
      toast({ title: parseApiError(e, "Lỗi"), variant: "error" });
    }
  };

  // Phân ca giao hàng cho 1 đơn
  const assignShift = async (orderId, shift, customerKey) => {
    try {
      const sameCustomerOrders = customerKey
        ? orders.filter((o) => (o.customer_id || `${o.customer_name}_${o.customer_phone}`) === customerKey)
        : [orders.find((o) => o.order_id === orderId)].filter(Boolean);
      await Promise.all(
        sameCustomerOrders.map((o) =>
          api.put(`/orders/${o.order_id}/assign-shift?shift=${shift}`)
        )
      );
      toast({ title: "Đã cập nhật ca giao hàng", variant: "success" });
      load();
    } catch (e) {
      toast({ title: parseApiError(e, "Lỗi"), variant: "error" });
    }
  };

  // Lọc đơn theo tất cả bộ lọc
  const filteredOrders = useMemo(() => {
    let list = orders;
    if (filterShipper) list = list.filter((o) => o.assigned_shipper_id === filterShipper);
    if (activeShift)   list = list.filter((o) => o.delivery_shift === activeShift);
    if (filterProduct) {
      const kw = filterProduct.toLowerCase();
      list = list.filter((o) => o.items?.some((it) => stripAccents(it.name ?? '').includes(stripAccents(kw))));
    }
    if (filterMinQty) {
      const minQ = Number(filterMinQty);
      list = list.filter((o) => {
        const total = o.items?.reduce((s, it) => s + (it.quantity || 0), 0) || 0;
        return total >= minQ;
      });
    }
    if (searchQ) {
      const kw = searchQ.toLowerCase();
      list = list.filter((o) =>
        stripAccents(o.customer_name ?? '').includes(stripAccents(kw)) ||
        o.customer_phone?.includes(kw) ||
        o.order_code?.toLowerCase().includes(kw)
      );
    }
    return list;
  }, [orders, filterShipper, activeShift, filterProduct, filterMinQty, searchQ]);

  // Group theo khách
  const groupedByCustomer = useMemo(() => {
    const map = {};
    filteredOrders.forEach((o) => {
      const key = o.customer_id || `${o.customer_name}_${o.customer_phone}`;
      if (!map[key]) map[key] = {
        key,
        customer_name: o.customer_name,
        customer_phone: o.customer_phone,
        customer_district: o.customer_district,
        customer_address: o.customer_address,
        orders: [],
      };
      map[key].orders.push(o);
    });
    return Object.values(map).map((g) => ({
      ...g,
      order_count: g.orders.length,
      group_total: g.orders.reduce((s, o) => s + (o.total || 0), 0),
      group_qty:   g.orders.reduce((s, o) => s + (o.items?.reduce((ss, it) => ss + (it.quantity || 0), 0) || 0), 0),
      is_unassigned: g.orders.some((o) => !o.assigned_shipper_id) ? 0 : 1,
      delivery_shift: g.orders[0]?.delivery_shift || "",
    }));
  }, [filteredOrders]);

  const { sorted: sortedGroups, handleSort, SortIcon } = useSort(groupedByCustomer, "is_unassigned", "asc");
  const toggleCustomer = (key) => setExpandedCustomers((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleProduct  = (key) => setExpandedProducts((prev)  => ({ ...prev, [key]: !prev[key] }));

  // ── Group theo sản phẩm ───────────────────────────────────────────────────
  // filteredOrders nhưng có thêm lọc theo filterProduct (tên sp cụ thể) và filterMinQty (SL của sp đó)
  const groupedByProduct = useMemo(() => {
    const map = {}; // productName → { rows: [{order, item, shipper, shift}] }
    filteredOrders.forEach((o) => {
      (o.items || []).forEach((it) => {
        const key = it.name || "Không tên";
        if (!map[key]) map[key] = { productName: key, rows: [], totalQty: 0 };
        map[key].rows.push({
          order_id:      o.order_id,
          order_code:    o.order_code,
          customer_name: o.customer_name,
          customer_phone:o.customer_phone,
          quantity:      it.quantity || 0,
          unit:          it.unit || "cái",
          shipper_id:    o.assigned_shipper_id,
          shipper_name:  shippers.find(s => s.user_id === o.assigned_shipper_id)?.name || "—",
          shift:         o.delivery_shift || "",
          order_ref:     o,
        });
        map[key].totalQty += it.quantity || 0;
      });
    });

    return Object.values(map).map((g) => {
      // Shipper nào giao sp này (distinct)
      const shipperSet = {};
      g.rows.forEach(r => {
        if (r.shipper_id) shipperSet[r.shipper_id] = r.shipper_name;
      });
      // Ca nào
      const shiftSet = new Set(g.rows.map(r => r.shift).filter(Boolean));
      // SL theo ca
      const qtyMorning   = g.rows.filter(r => r.shift === SHIFT_MORNING).reduce((s, r) => s + r.quantity, 0);
      const qtyAfternoon = g.rows.filter(r => r.shift === SHIFT_AFTERNOON).reduce((s, r) => s + r.quantity, 0);
      const qtyUnset     = g.rows.filter(r => !r.shift).reduce((s, r) => s + r.quantity, 0);
      return {
        ...g,
        shippers: Object.values(shipperSet),
        shifts: [...shiftSet],
        qtyMorning,
        qtyAfternoon,
        qtyUnset,
        customerCount: new Set(g.rows.map(r => r.customer_name)).size,
      };
    }).sort((a, b) => b.totalQty - a.totalQty);
  }, [filteredOrders, shippers]);

  // KPI tổng
  const totalOrders    = orders.length;
  const assignedOrders = orders.filter((o) => o.assigned_shipper_id).length;
  const totalAmount    = filteredOrders.reduce((s, o) => s + (o.total || 0), 0);

  // KPI từng ca
  const shiftStats = useMemo(() => SHIFTS.map((sh) => {
    const shiftOrders = orders.filter((o) => o.delivery_shift === sh.id);
    return {
      ...sh,
      count:    shiftOrders.length,
      amount:   shiftOrders.reduce((s, o) => s + (o.total || 0), 0),
      qty:      shiftOrders.reduce((s, o) => s + (o.items?.reduce((ss, it) => ss + (it.quantity || 0), 0) || 0), 0),
      assigned: shiftOrders.filter((o) => o.assigned_shipper_id).length,
    };
  }), [orders]);

  // Tất cả chủng loại sản phẩm trong ngày
  const productNames = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => o.items?.forEach((it) => it.name && set.add(it.name)));
    return [...set].sort();
  }, [orders]);

  // In danh sách giao theo ca
  const handlePrint = (shift) => {
    const printOrders = shift ? orders.filter((o) => o.delivery_shift === shift) : filteredOrders;
    const shiftLabel  = shift ? SHIFTS.find(s => s.id === shift)?.label : "Tất cả";
    const shiftTime   = shift ? SHIFTS.find(s => s.id === shift)?.time  : "";

    // Group theo khách
    const grpMap = {};
    printOrders.forEach((o) => {
      const key = o.customer_id || `${o.customer_name}_${o.customer_phone}`;
      if (!grpMap[key]) grpMap[key] = { customer_name: o.customer_name, customer_phone: o.customer_phone, customer_district: o.customer_district, customer_address: o.customer_address, orders: [] };
      grpMap[key].orders.push(o);
    });
    const groups = Object.values(grpMap);

    let stt = 0;
    const rows = groups.map((group) => {
      stt++;
      const groupTotal = group.orders.reduce((s, o) => s + (o.total || 0), 0);
      const shipperName = shippers.find((s) => s.user_id === group.orders[0]?.assigned_shipper_id)?.name || "—";
      const payMethods  = [...new Set(group.orders.map((o) => PAY_LABEL[o.payment_method] || o.payment_method))].join(", ");
      const address     = [group.customer_district, group.customer_address].filter(Boolean).join(", ") || "—";
      const shiftBadge  = group.orders[0]?.delivery_shift === SHIFT_MORNING ? "☀️ Sáng" : group.orders[0]?.delivery_shift === SHIFT_AFTERNOON ? "🌤️ Chiều" : "—";

      const itemMap = {};
      group.orders.forEach((o) => {
        o.items?.forEach((it) => { itemMap[it.name] = (itemMap[it.name] || 0) + it.quantity; });
      });
      const itemsStr = Object.entries(itemMap).map(([name, qty]) => `${name} ×${qty}`).join(", ");
      const totalQty = Object.values(itemMap).reduce((s, q) => s + q, 0);

      return `<tr>
        <td style="text-align:center">${stt}</td>
        <td><strong>${group.customer_name || "—"}</strong><br/><small>${group.customer_phone || ""}</small></td>
        <td>${address}</td>
        <td>${itemsStr}<br/><small style="color:#888">Tổng SL: ${totalQty}</small></td>
        <td class="num"><strong>${groupTotal.toLocaleString("vi-VN")}₫</strong></td>
        <td>${payMethods}</td>
        <td>${shipperName}</td>
        <td style="text-align:center">${shiftBadge}</td>
      </tr>`;
    }).join("");

    const totalQtyAll = printOrders.reduce((s, o) => s + (o.items?.reduce((ss, it) => ss + (it.quantity || 0), 0) || 0), 0);
    const totalAmtAll = printOrders.reduce((s, o) => s + (o.total || 0), 0);

    const html = `<!DOCTYPE html>
<html lang="vi">
<head>
<meta charset="UTF-8"/>
<title>Danh sách giao hàng ${shiftLabel} — ${filterDate}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
  h2 { margin: 0 0 2px; font-size: 16px; }
  p.sub { color:#555; margin:0 0 10px; font-size:11px; }
  table { width:100%; border-collapse:collapse; }
  th,td { border:1px solid #ccc; padding:5px 7px; text-align:left; vertical-align:top; }
  th { background:#e8f5e9; font-size:10px; text-transform:uppercase; font-weight:bold; }
  td.num { text-align:right; white-space:nowrap; font-family:monospace; }
  tr:nth-child(even) td { background:#f9f9f9; }
  tfoot td { font-weight:bold; background:#d4edda; }
  @media print { body { margin:8mm; } }
</style>
</head>
<body>
<h2>📋 Danh sách giao hàng — ${shiftLabel}${shiftTime ? ` (${shiftTime})` : ""} — ${filterDate}</h2>
<p class="sub">${printOrders.length} đơn · ${groups.length} khách · Tổng SL: ${totalQtyAll} cái · Tổng tiền: <strong>${totalAmtAll.toLocaleString("vi-VN")}₫</strong></p>
<table>
  <thead>
    <tr>
      <th style="width:30px">STT</th>
      <th>Khách hàng</th>
      <th>Địa chỉ</th>
      <th>Hàng giao</th>
      <th style="text-align:right">Tổng tiền</th>
      <th>Thanh toán</th>
      <th>Shipper</th>
      <th>Ca</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr>
      <td colspan="4" style="text-align:right">TỔNG CỘNG (${groups.length} khách · ${printOrders.length} đơn · ${totalQtyAll} cái)</td>
      <td class="num">${totalAmtAll.toLocaleString("vi-VN")}₫</td>
      <td colspan="3"></td>
    </tr>
  </tfoot>
</table>
<script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  };

  // In danh sách theo sản phẩm + nhân viên (viewMode === "product")
  const handlePrintProduct = () => {
    const shiftLabel = activeShift ? SHIFTS.find(s => s.id === activeShift)?.label : "Tất cả ca";
    const shiftTime  = activeShift ? SHIFTS.find(s => s.id === activeShift)?.time  : "";
    const shipperLabel = filterShipper ? shippers.find(s => s.user_id === filterShipper)?.name : "";

    // Tính tổng toàn bộ
    const grandTotalQty = groupedByProduct.reduce((s, g) => s + g.totalQty, 0);
    const grandOrders   = new Set(filteredOrders.map(o => o.order_id)).size;

    // Rows tổng hợp theo sản phẩm
    const productRows = groupedByProduct.map((g, idx) => {
      const shipperNames = g.shippers.length > 0 ? g.shippers.join(", ") : "—";
      const caDisplay = [
        g.qtyMorning   > 0 ? `☀️ Sáng: ${g.qtyMorning}` : "",
        g.qtyAfternoon > 0 ? `🌤️ Chiều: ${g.qtyAfternoon}` : "",
        g.qtyUnset     > 0 ? `⬜ Chưa ca: ${g.qtyUnset}` : "",
      ].filter(Boolean).join(" · ");

      // Sub-rows chi tiết khách
      const subRows = g.rows.map(r => {
        const shiftTxt = r.shift === SHIFT_MORNING ? "☀️ Sáng" : r.shift === SHIFT_AFTERNOON ? "🌤️ Chiều" : "—";
        return `<tr class="sub-row">
          <td></td>
          <td style="padding-left:20px;color:#444">↳ ${r.customer_name || "—"}</td>
          <td style="color:#666;font-size:10px">${r.customer_phone || ""}</td>
          <td style="text-align:center;font-size:11px">${r.quantity} ${r.unit}</td>
          <td style="font-size:10px">${r.shipper_name !== "—" ? r.shipper_name : '<span style="color:#dc2626">Chưa phân</span>'}</td>
          <td style="font-size:10px;text-align:center">${shiftTxt}</td>
          <td style="font-size:10px;color:#777">${r.order_code || ""}</td>
        </tr>`;
      }).join("");

      return `<tr class="product-row">
        <td style="text-align:center;font-weight:bold">${idx + 1}</td>
        <td><strong>${g.productName}</strong></td>
        <td style="font-size:10px;color:#555">${g.customerCount} khách</td>
        <td style="text-align:center"><strong class="qty">${g.totalQty}</strong> <span style="font-size:10px;color:#888">${g.rows[0]?.unit || "cái"}</span></td>
        <td style="font-size:11px">${shipperNames}</td>
        <td style="font-size:10px">${caDisplay || "—"}</td>
        <td></td>
      </tr>
      ${subRows}`;
    }).join("");

    const html = `<!DOCTYPE html>
<html lang="vi">
<head>
<meta charset="UTF-8"/>
<title>Danh sách giao theo sản phẩm — ${filterDate}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #222; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #2D4A22; padding-bottom:10px; margin-bottom:14px; }
  .shop { font-size:15px; font-weight:bold; color:#2D4A22; }
  h2 { margin:0 0 2px; font-size:16px; }
  p.sub { color:#555; margin:0 0 10px; font-size:11px; }
  .filters { background:#f5f5f5; border:1px solid #ddd; border-radius:5px; padding:6px 12px; margin-bottom:12px; font-size:11px; display:flex; gap:20px; flex-wrap:wrap; }
  .filters span { color:#555; }
  .filters strong { color:#2D4A22; }
  table { width:100%; border-collapse:collapse; margin-bottom:10px; }
  th { background:#2D4A22; color:white; font-size:10px; text-transform:uppercase; padding:5px 7px; text-align:left; }
  td { padding:5px 7px; vertical-align:middle; border-bottom:1px solid #eee; }
  tr.product-row td { background:#f0f7ee; border-top:2px solid #c8e6c9; font-size:12px; }
  tr.sub-row td { background:#fff; font-size:11px; }
  tr.sub-row:hover td { background:#fafff8; }
  .qty { font-size:15px; color:#2D4A22; }
  tfoot td { font-weight:bold; background:#d4edda; border-top:2px solid #2D4A22; }
  .kpi { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:14px; }
  .kpi-box { border:1px solid #ddd; border-radius:5px; padding:6px 10px; text-align:center; }
  .kpi-box .val { font-size:16px; font-weight:bold; color:#2D4A22; }
  .kpi-box .lbl { font-size:9px; color:#888; text-transform:uppercase; margin-top:2px; }
  .footer { margin-top:14px; font-size:10px; color:#888; border-top:1px solid #ddd; padding-top:8px; display:flex; justify-content:space-between; }
  @media print { body { margin:8mm; } }
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="shop">${window.__SHOP_NAME__ || "Tiệm Bánh Bao"}</div>
    <div style="font-size:10px;color:#666">Ngày in: ${new Date().toLocaleDateString("vi-VN")}</div>
  </div>
  <div style="text-align:right;font-size:11px;color:#555">
    Ngày giao: <strong>${filterDate}</strong><br/>
    Ca: <strong>${shiftLabel}${shiftTime ? ` (${shiftTime})` : ""}</strong>
    ${shipperLabel ? `<br/>Shipper: <strong>${shipperLabel}</strong>` : ""}
  </div>
</div>

<h2>📦 Danh sách giao hàng theo sản phẩm</h2>
<p class="sub">${groupedByProduct.length} loại sản phẩm · ${grandOrders} đơn · ${grandTotalQty} sản phẩm</p>

<div class="filters">
  <span>📅 Ngày: <strong>${filterDate}</strong></span>
  <span>🚀 Ca: <strong>${shiftLabel}${shiftTime ? ` (${shiftTime})` : ""}</strong></span>
  ${shipperLabel ? `<span>👤 Shipper: <strong>${shipperLabel}</strong></span>` : ""}
</div>

<div class="kpi">
  <div class="kpi-box"><div class="val">${groupedByProduct.length}</div><div class="lbl">Loại SP</div></div>
  <div class="kpi-box"><div class="val">${grandTotalQty}</div><div class="lbl">Tổng SL</div></div>
  <div class="kpi-box"><div class="val">${groupedByProduct.reduce((s,g)=>s+g.qtyMorning,0)}</div><div class="lbl">☀️ Ca sáng</div></div>
  <div class="kpi-box"><div class="val">${groupedByProduct.reduce((s,g)=>s+g.qtyAfternoon,0)}</div><div class="lbl">🌤️ Ca chiều</div></div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:30px">STT</th>
      <th>Tên sản phẩm</th>
      <th>Số khách</th>
      <th style="text-align:center">Tổng SL</th>
      <th>NV giao hàng</th>
      <th>Phân ca</th>
      <th>Mã đơn</th>
    </tr>
  </thead>
  <tbody>${productRows}</tbody>
  <tfoot>
    <tr>
      <td colspan="3" style="text-align:right">TỔNG CỘNG (${groupedByProduct.length} loại · ${grandOrders} đơn)</td>
      <td style="text-align:center"><strong class="qty">${grandTotalQty}</strong></td>
      <td colspan="3"></td>
    </tr>
  </tfoot>
</table>

<div class="footer">
  <span>In lúc: ${new Date().toLocaleString("vi-VN")} · ${window.__SHOP_NAME__ || "Tiệm Bánh Bao"}</span>
  <span>Ngày giao: ${filterDate} · ${shiftLabel}</span>
</div>
<script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  };

  // Mở preview in bill cho 1 đơn cụ thể
  const handlePrintOrderA5 = (order) => {
    setPrintOrder(order);
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="delivery-page">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bike size={28} className="text-bamboo" /> {t("delivery.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-1">
            {totalOrders} đơn cần giao · {assignedOrders} đã phân công · {totalOrders - assignedOrders} chưa phân
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => handlePrint(SHIFT_MORNING)}>
            <Sun size={14} className="text-amber-500" /> In Ca Sáng
          </Button>
          <Button variant="outline" onClick={() => handlePrint(SHIFT_AFTERNOON)}>
            <Sunset size={14} className="text-blue-500" /> In Ca Chiều
          </Button>
          <Button variant="outline" onClick={() => handlePrint(null)}>
            <Printer size={14} /> In Tất Cả
          </Button>
        </div>
      </div>

      {/* Thống kê 2 ca */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SHIFTS.map((sh) => {
          const stat = shiftStats.find(s => s.id === sh.id);
          const isActive = activeShift === sh.id;
          return (
            <button
              key={sh.id}
              onClick={() => setActiveShift(isActive ? null : sh.id)}
              className={cn(
                "relative overflow-hidden rounded-xl p-4 text-left transition-all border-2",
                sh.color === "amber"
                  ? isActive ? "bg-amber-500 border-amber-500 shadow-lg" : "bg-amber-50 border-amber-200 hover:border-amber-400"
                  : isActive ? "bg-blue-500 border-blue-500 shadow-lg" : "bg-blue-50 border-blue-200 hover:border-blue-400"
              )}
            >
              <div className={cn("absolute -top-3 -right-3 w-16 h-16 rounded-full opacity-20",
                sh.color === "amber" ? "bg-amber-400" : "bg-blue-400")} />
              <div className={cn("flex items-center gap-2 mb-2 font-bold text-sm",
                isActive ? "text-white" : sh.color === "amber" ? "text-amber-700" : "text-blue-700")}>
                <sh.Icon size={16} /> {sh.label}
                <span className={cn("text-xs font-normal", isActive ? "text-white/70" : "text-ink-muted")}>
                  {sh.time}
                </span>
              </div>
              <div className={cn("grid grid-cols-3 gap-2 text-center")}>
                {[
                  { label: "Đơn", value: stat?.count ?? 0 },
                  { label: "Sản phẩm", value: stat?.qty ?? 0 },
                  { label: "Doanh thu", value: formatVND(stat?.amount ?? 0) },
                ].map((kpi) => (
                  <div key={kpi.label}>
                    <div className={cn("font-bold text-base", isActive ? "text-white" : "text-ink")}>{kpi.value}</div>
                    <div className={cn("text-[10px]", isActive ? "text-white/70" : "text-ink-muted")}>{kpi.label}</div>
                  </div>
                ))}
              </div>
              {isActive && (
                <div className="mt-2 text-[10px] text-white/80 text-right">▼ Đang lọc ca này · Bấm để bỏ lọc</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Bộ lọc */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label>Ngày giao</Label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full h-9 px-3 text-sm bg-white border border-border rounded-md"
                data-testid="delivery-date"
              />
            </div>
            <div>
              <Label>Nhân viên giao hàng</Label>
              <Select value={filterShipper} onChange={(e) => setFilterShipper(e.target.value)} data-testid="delivery-shipper-filter">
                <option value="">— Tất cả shipper —</option>
                {shippers.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
              </Select>
            </div>
            <div>
              <Label>Chủng loại sản phẩm</Label>
              <Select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)}>
                <option value="">— Tất cả loại —</option>
                {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
            <div>
              <Label>Số lượng tối thiểu</Label>
              <Input
                type="number"
                min={1}
                value={filterMinQty}
                onChange={(e) => setFilterMinQty(e.target.value)}
                placeholder="VD: 10"
              />
            </div>
            <div>
              <Label>Tìm kiếm khách / mã đơn</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-ink-muted" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Tên, SĐT, mã đơn..."
                  className="pl-8"
                />
              </div>
            </div>
          </div>
          {/* Active filters summary */}
          {(filterShipper || activeShift || filterProduct || filterMinQty || searchQ) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
              <span className="text-xs text-ink-muted flex items-center gap-1"><Filter size={11} /> Đang lọc:</span>
              {activeShift && <span className="text-xs bg-cream px-2 py-0.5 rounded-full">{SHIFTS.find(s=>s.id===activeShift)?.label}</span>}
              {filterShipper && <span className="text-xs bg-cream px-2 py-0.5 rounded-full">{shippers.find(s=>s.user_id===filterShipper)?.name}</span>}
              {filterProduct && <span className="text-xs bg-cream px-2 py-0.5 rounded-full">{filterProduct}</span>}
              {filterMinQty && <span className="text-xs bg-cream px-2 py-0.5 rounded-full">SL ≥ {filterMinQty}</span>}
              {searchQ && <span className="text-xs bg-cream px-2 py-0.5 rounded-full">"{searchQ}"</span>}
              <button
                onClick={() => { setFilterShipper(""); setActiveShift(null); setFilterProduct(""); setFilterMinQty(""); setSearchQ(""); }}
                className="text-xs text-red-500 hover:underline ml-1"
              >
                Xoá bộ lọc
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI tóm tắt hiện tại */}
      {filteredOrders.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Tổng đơn",    value: filteredOrders.length, from: "#2D4A22", to: "#4a7a38", shadow: "rgba(45,74,34,0.3)" },
            { label: "Khách hàng",  value: groupedByCustomer.length, from: "#1d4ed8", to: "#3b82f6", shadow: "rgba(29,78,216,0.3)" },
            { label: "Đã phân công", value: filteredOrders.filter(o=>o.assigned_shipper_id).length, from: "#d97706", to: "#f59e0b", shadow: "rgba(217,119,6,0.3)" },
            { label: "Tổng tiền",   value: formatVND(totalAmount), from: "#D95D39", to: "#e8804f", shadow: "rgba(217,93,57,0.3)" },
          ].map((kpi) => (
            <div key={kpi.label} style={{ background: `linear-gradient(135deg,${kpi.from},${kpi.to})`, boxShadow: `0 6px 20px ${kpi.shadow}` }} className="relative overflow-hidden rounded-xl p-4">
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
              <div className="text-xs uppercase tracking-widest font-semibold text-white/65 mb-1">{kpi.label}</div>
              <div className="font-heading text-2xl font-bold text-white leading-none">{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Bảng danh sách */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle>
              {activeShift ? `${SHIFTS.find(s=>s.id===activeShift)?.label} (${SHIFTS.find(s=>s.id===activeShift)?.time})` : "Tất cả ca"} — {filteredOrders.length} đơn · {groupedByCustomer.length} khách
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Nút in theo view đang chọn */}
              {viewMode === "product" ? (
                <Button variant="outline" size="sm" onClick={handlePrintProduct}>
                  <Printer size={13} /> In theo sản phẩm
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => handlePrint(activeShift || null)}>
                  <Printer size={13} /> In danh sách
                </Button>
              )}
              {/* Toggle view */}
              <div className="flex items-center gap-1 bg-cream rounded-xl p-1 border border-border">
              <button
                onClick={() => setViewMode("customer")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                  viewMode === "customer" ? "bg-white shadow text-bamboo border border-border" : "text-ink-muted hover:text-ink"
                )}
              >
                <User size={13} /> Theo khách hàng
              </button>
              <button
                onClick={() => setViewMode("product")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                  viewMode === "product" ? "bg-white shadow text-bamboo border border-border" : "text-ink-muted hover:text-ink"
                )}
              >
                <Layers size={13} /> Theo sản phẩm
              </button>
            </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="!p-0">

          {/* ── VIEW: THEO KHÁCH HÀNG ── */}
          {viewMode === "customer" && (loading ? (
            <div className="py-12 text-center text-sm text-ink-muted">Đang tải...</div>
          ) : groupedByCustomer.length === 0 ? (
            <div className="py-12 text-center text-sm text-ink-muted">Không có đơn nào phù hợp</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream/60 text-[11px] uppercase text-ink-muted border-b border-border">
                  <tr>
                    <th className="text-left py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("customer_name")}>Khách hàng {SortIcon("customer_name")}</th>
                    <th className="text-left py-2.5 px-4 font-semibold">SĐT</th>
                    <th className="text-left py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("customer_district")}>Địa chỉ {SortIcon("customer_district")}</th>
                    <th className="text-center py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("group_qty")}>SL {SortIcon("group_qty")}</th>
                    <th className="text-center py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("order_count")}>Đơn {SortIcon("order_count")}</th>
                    <th className="text-right py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("group_total")}>Tổng tiền {SortIcon("group_total")}</th>
                    <th className="text-center py-2.5 px-4 font-semibold">Thanh toán</th>
                    <th className="text-left py-2.5 px-4 font-semibold cursor-pointer hover:text-ink" onClick={() => handleSort("is_unassigned")}>Shipper {SortIcon("is_unassigned")}</th>
                    <th className="text-center py-2.5 px-4 font-semibold">Ca giao</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedGroups.map((group) => {
                    const multiOrder  = group.orders.length > 1;
                    const isExpanded  = expandedCustomers[group.key] === true;
                    const firstOrder  = group.orders[0];
                    const shipperName = shippers.find((s) => s.user_id === firstOrder?.assigned_shipper_id)?.name || "—";
                    const payMethods  = [...new Set(group.orders.map((o) => PAY_LABEL[o.payment_method] || o.payment_method))].join(", ");
                    const currentShift = firstOrder?.delivery_shift || "";

                    return (
                      <React.Fragment key={group.key}>
                        <tr className={cn("hover:bg-cream/30 transition-colors", multiOrder && "bg-amber-50/40")} data-testid={`delivery-group-${group.key}`}>

                          {/* Khách */}
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold", multiOrder ? "bg-amber-500" : "bg-bamboo")}>
                                {multiOrder ? group.orders.length : <User size={12} />}
                              </div>
                              <span className="font-semibold">{group.customer_name || "—"}</span>
                            </div>
                          </td>

                          {/* SĐT */}
                          <td className="py-2.5 px-4">
                            {group.customer_phone
                              ? <a href={`tel:${group.customer_phone}`} className="text-bamboo hover:underline flex items-center gap-1"><Phone size={11} /> {group.customer_phone}</a>
                              : "—"}
                          </td>

                          {/* Địa chỉ */}
                          <td className="py-2.5 px-4 text-ink-secondary max-w-[200px]">
                            <div className="flex items-start gap-1">
                              {(group.customer_district || group.customer_address) && <MapPin size={11} className="mt-0.5 shrink-0 text-ink-muted" />}
                              <span className="line-clamp-2">{[group.customer_district, group.customer_address].filter(Boolean).join(", ") || "—"}</span>
                            </div>
                          </td>

                          {/* SL */}
                          <td className="py-2.5 px-4 text-center font-mono font-semibold text-ink">{group.group_qty}</td>

                          {/* Số đơn */}
                          <td className="py-2.5 px-4 text-center">
                            {multiOrder
                              ? <button onClick={() => toggleCustomer(group.key)} className="inline-flex items-center gap-1 text-amber-600 font-semibold hover:underline">
                                  {group.orders.length} đơn {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                              : <span className="text-ink-muted">1 đơn</span>}
                          </td>

                          {/* Tổng tiền */}
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-bamboo">{formatVND(group.group_total)}</td>

                          {/* Thanh toán */}
                          <td className="py-2.5 px-4 text-center">
                            <Badge variant={group.orders.some(o => o.payment_method === "debt") ? "lowstock" : "regular"}>
                              {payMethods}
                            </Badge>
                          </td>

                          {/* Shipper */}
                          <td className="py-2.5 px-4">
                            {!firstOrder?.assigned_shipper_id && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600 mb-1">Chưa phân</span>
                            )}
                            <Select
                              value={firstOrder?.assigned_shipper_id || ""}
                              onChange={(e) => assignShipper(firstOrder.order_id, e.target.value, group.key)}
                              className="h-7 text-[11px] min-w-[110px] block"
                              data-testid={`delivery-assign-${firstOrder?.order_id}`}
                            >
                              <option value="">— Chọn shipper —</option>
                              {shippers.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                            </Select>
                          </td>

                          {/* Ca giao hàng + nút in */}
                          <td className="py-2.5 px-4 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <ShiftBadge shift={currentShift} />
                              <Select
                                value={currentShift}
                                onChange={(e) => assignShift(firstOrder.order_id, e.target.value, group.key)}
                                className="h-6 text-[10px] min-w-[90px] block"
                              >
                                <option value="">— Chọn ca —</option>
                                <option value={SHIFT_MORNING}>☀️ Ca Sáng (8–12:30)</option>
                                <option value={SHIFT_AFTERNOON}>🌤️ Ca Chiều (12:30–18h)</option>
                              </Select>
                              {/* Nút in + xem chi tiết */}
                              <div className="flex items-center gap-1 mt-0.5">
                                {!multiOrder && (
                                  <Link to={`/orders/${firstOrder?.order_id}`}>
                                    <Button variant="ghost" size="icon" title="Xem chi tiết đơn"><ExternalLink size={13} /></Button>
                                  </Link>
                                )}
                                {!multiOrder && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="In phiếu A5"
                                    onClick={() => handlePrintOrderA5(firstOrder)}
                                    className="text-bamboo hover:bg-bamboo/10"
                                  >
                                    <Printer size={13} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* Sub-rows khi mở rộng */}
                        {multiOrder && isExpanded && group.orders.map((o) => {
                          const subQty = o.items?.reduce((s, it) => s + (it.quantity || 0), 0) || 0;
                          return (
                            <tr key={o.order_id} className="bg-amber-50/60 border-l-4 border-l-amber-400 text-xs" data-testid={`delivery-row-${o.order_id}`}>
                              <td className="py-2 pl-12 pr-4">
                                <span className="font-mono font-bold text-amber-700">{o.order_code}</span>
                                {o.note && <div className="text-ink-muted italic mt-0.5">{o.note}</div>}
                              </td>
                              <td className="py-2 px-4 text-ink-muted">{formatDateTime(o.created_at)}</td>
                              <td className="py-2 px-4" colSpan={1}>
                                <div className="flex flex-wrap gap-x-2">
                                  {o.items?.map((it, i) => (
                                    <span key={i} className="flex items-center gap-0.5">
                                      <Package size={9} className="text-ink-muted" />
                                      {it.name} <span className="font-mono text-ink-muted">×{it.quantity}</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="py-2 px-4 text-center font-mono font-semibold">{subQty}</td>
                              <td className="py-2 px-4 text-center text-ink-muted">1</td>
                              <td className="py-2 px-4 text-right font-mono font-bold text-bamboo">{formatVND(o.total)}</td>
                              <td className="py-2 px-4 text-center">
                                <Badge variant={o.payment_method === "debt" ? "lowstock" : "regular"}>
                                  {PAY_LABEL[o.payment_method] || o.payment_method}
                                </Badge>
                              </td>
                              <td className="py-2 px-4">
                                <Select
                                  value={o.assigned_shipper_id || ""}
                                  onChange={(e) => assignShipper(o.order_id, e.target.value, group.key)}
                                  className="h-7 text-[11px] min-w-[110px] block"
                                >
                                  <option value="">— Chọn shipper —</option>
                                  {shippers.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                                </Select>
                              </td>
                              <td className="py-2 px-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <ShiftBadge shift={o.delivery_shift} />
                                  <Select
                                    value={o.delivery_shift || ""}
                                    onChange={(e) => assignShift(o.order_id, e.target.value, null)}
                                    className="h-6 text-[10px] min-w-[90px] block"
                                  >
                                    <option value="">— Chọn ca —</option>
                                    <option value={SHIFT_MORNING}>☀️ Sáng</option>
                                    <option value={SHIFT_AFTERNOON}>🌤️ Chiều</option>
                                  </Select>
                                  {/* Nút in + xem chi tiết */}
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <Link to={`/orders/${o.order_id}`}>
                                      <Button variant="ghost" size="icon" title="Xem chi tiết đơn"><ExternalLink size={13} /></Button>
                                    </Link>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      title="In phiếu A5"
                                      onClick={() => handlePrintOrderA5(o)}
                                      className="text-bamboo hover:bg-bamboo/10"
                                    >
                                      <Printer size={13} />
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {/* end viewMode === customer */}

          {/* ── VIEW: THEO SẢN PHẨM ── */}
          {viewMode === "product" && (
            loading ? (
              <div className="py-12 text-center text-sm text-ink-muted">Đang tải...</div>
            ) : groupedByProduct.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-muted">Không có sản phẩm nào</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream/60 text-[11px] uppercase text-ink-muted border-b border-border">
                    <tr>
                      <th className="text-left py-2.5 px-4">Tên sản phẩm</th>
                      <th className="text-center py-2.5 px-4 text-bamboo font-bold">Tổng SL hôm nay</th>
                      <th className="text-center py-2.5 px-4">
                        <span className="flex items-center justify-center gap-1"><Sun size={11} className="text-amber-500"/>Ca Sáng</span>
                      </th>
                      <th className="text-center py-2.5 px-4">
                        <span className="flex items-center justify-center gap-1"><Sunset size={11} className="text-blue-500"/>Ca Chiều</span>
                      </th>
                      <th className="text-center py-2.5 px-4 text-ink-muted">Chưa ca</th>
                      <th className="text-left py-2.5 px-4">NV giao hàng</th>
                      <th className="text-center py-2.5 px-4">Khách</th>
                      <th className="py-2.5 px-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {groupedByProduct.map((g) => {
                      const isExpanded = expandedProducts[g.productName];
                      return (
                        <React.Fragment key={g.productName}>
                          {/* Hàng tổng sản phẩm */}
                          <tr
                            className="hover:bg-cream/30 cursor-pointer transition-colors"
                            onClick={() => toggleProduct(g.productName)}
                          >
                            {/* Tên SP */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-bamboo/10 flex items-center justify-center shrink-0">
                                  <Package size={14} className="text-bamboo" />
                                </div>
                                <span className="font-semibold text-ink">{g.productName}</span>
                              </div>
                            </td>

                            {/* Tổng SL */}
                            <td className="py-3 px-4 text-center">
                              <span className="text-2xl font-heading font-bold text-bamboo">{g.totalQty}</span>
                              <span className="text-xs text-ink-muted ml-1">{g.rows[0]?.unit || "cái"}</span>
                            </td>

                            {/* Ca sáng */}
                            <td className="py-3 px-4 text-center">
                              {g.qtyMorning > 0 ? (
                                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 font-bold px-2.5 py-1 rounded-full text-sm">
                                  <Sun size={11}/> {g.qtyMorning}
                                </span>
                              ) : <span className="text-ink-muted text-xs">—</span>}
                            </td>

                            {/* Ca chiều */}
                            <td className="py-3 px-4 text-center">
                              {g.qtyAfternoon > 0 ? (
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 font-bold px-2.5 py-1 rounded-full text-sm">
                                  <Sunset size={11}/> {g.qtyAfternoon}
                                </span>
                              ) : <span className="text-ink-muted text-xs">—</span>}
                            </td>

                            {/* Chưa ca */}
                            <td className="py-3 px-4 text-center">
                              {g.qtyUnset > 0 ? (
                                <span className="text-ink-muted font-semibold">{g.qtyUnset}</span>
                              ) : <span className="text-green-600 text-xs">✓</span>}
                            </td>

                            {/* NV giao */}
                            <td className="py-3 px-4">
                              {g.shippers.length === 0 ? (
                                <span className="text-xs text-red-500 font-semibold">Chưa phân</span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {g.shippers.map((s, i) => (
                                    <span key={i} className="text-xs bg-cream px-2 py-0.5 rounded-full font-medium border border-border">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>

                            {/* Số khách */}
                            <td className="py-3 px-4 text-center text-ink-muted text-sm">{g.customerCount} KH</td>

                            {/* Expand */}
                            <td className="py-3 px-3 text-center">
                              <button className="text-ink-muted hover:text-ink">
                                {isExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                              </button>
                            </td>
                          </tr>

                          {/* Sub-rows: danh sách khách đặt sản phẩm này */}
                          {isExpanded && (
                            <tr className="bg-cream/20">
                              <td colSpan={8} className="p-0">
                                <div className="px-6 py-3">
                                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1">
                                    <User size={10}/> Chi tiết khách hàng đặt {g.productName}
                                  </div>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-ink-muted border-b border-border/60">
                                        <th className="text-left pb-1.5 font-semibold">Khách hàng</th>
                                        <th className="text-left pb-1.5 font-semibold">SĐT</th>
                                        <th className="text-center pb-1.5 font-semibold">Số lượng</th>
                                        <th className="text-left pb-1.5 font-semibold">NV giao</th>
                                        <th className="text-center pb-1.5 font-semibold">Ca giao</th>
                                        <th className="text-left pb-1.5 font-semibold">Mã đơn</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {g.rows.map((r, i) => (
                                        <tr key={i} className="border-t border-border/40 hover:bg-white/60">
                                          <td className="py-2 font-medium">{r.customer_name || "—"}</td>
                                          <td className="py-2 text-ink-muted">
                                            {r.customer_phone
                                              ? <a href={`tel:${r.customer_phone}`} className="text-bamboo hover:underline">{r.customer_phone}</a>
                                              : "—"}
                                          </td>
                                          <td className="py-2 text-center">
                                            <span className="font-mono font-bold text-bamboo text-sm">{r.quantity}</span>
                                            <span className="text-ink-muted ml-0.5">{r.unit}</span>
                                          </td>
                                          <td className="py-2">
                                            {r.shipper_name !== "—" ? (
                                              <span className="bg-cream px-2 py-0.5 rounded-full border border-border">{r.shipper_name}</span>
                                            ) : (
                                              <span className="text-red-500 font-semibold">Chưa phân</span>
                                            )}
                                          </td>
                                          <td className="py-2 text-center">
                                            <ShiftBadge shift={r.shift} />
                                          </td>
                                          <td className="py-2">
                                            <Link to={`/orders/${r.order_id}`} className="font-mono text-bamboo hover:underline">
                                              {r.order_code}
                                            </Link>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    {/* Tổng SL sản phẩm này */}
                                    <tfoot className="border-t border-border/60">
                                      <tr className="font-semibold text-ink">
                                        <td colSpan={2} className="pt-2 text-right text-ink-muted">Tổng:</td>
                                        <td className="pt-2 text-center">
                                          <span className="font-mono font-bold text-bamboo">{g.totalQty}</span>
                                          <span className="text-ink-muted ml-0.5">{g.rows[0]?.unit || "cái"}</span>
                                        </td>
                                        <td colSpan={3} className="pt-2">
                                          <span className="text-[10px] text-ink-muted">
                                            {g.qtyMorning > 0 && `☀️ Sáng: ${g.qtyMorning}  `}
                                            {g.qtyAfternoon > 0 && `🌤️ Chiều: ${g.qtyAfternoon}  `}
                                            {g.qtyUnset > 0 && `⚪ Chưa ca: ${g.qtyUnset}`}
                                          </span>
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>

                  {/* Footer tổng toàn bộ */}
                  <tfoot className="border-t-2 border-border bg-cream/40">
                    <tr>
                      <td className="py-3 px-4 font-bold text-sm">
                        {groupedByProduct.length} loại sản phẩm
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-heading font-bold text-bamboo text-lg">
                          {groupedByProduct.reduce((s, g) => s + g.totalQty, 0)}
                        </span>
                        <span className="text-xs text-ink-muted ml-1">cái</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-bold text-amber-600">
                          {groupedByProduct.reduce((s, g) => s + g.qtyMorning, 0)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-bold text-blue-600">
                          {groupedByProduct.reduce((s, g) => s + g.qtyAfternoon, 0)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-ink-muted font-semibold">
                        {groupedByProduct.reduce((s, g) => s + g.qtyUnset, 0)}
                      </td>
                      <td colSpan={3} className="py-3 px-4 text-xs text-ink-muted">
                        {filteredOrders.length} đơn · {filteredOrders.filter(o => o.assigned_shipper_id).length} đã phân công
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          )}

        </CardContent>
      </Card>
      {/* Modal in bill đơn hàng */}
      {printOrder && (
        <PrintPreview
          open={true}
          onClose={() => setPrintOrder(null)}
          title={`Phiếu Xuất Kho A5 — ${printOrder.order_code}`}
          printId="delivery-print-pxk"
          pdfFilename={`${printOrder.order_code}.pdf`}
          pdfFormat="a5"
        >
          <PhieuXuatKho order={printOrder} shop={{ ...shopInfo, pxk_paper_size: "a5" }} />
        </PrintPreview>
      )}
    </div>
  );
}